<template>
  <div class="m-list">
    <router-link to="/m/retoure/neu" class="m-ret__new">
      <i class="material-icons">add_a_photo</i>
      <span>{{ $t("returns.new") }}</span>
    </router-link>

    <div class="m-search">
      <i class="material-icons">search</i>
      <input v-model="keywords" :placeholder="$t('returns.searchPlaceholder')" type="search" @input="onSearchInput" />
      <button v-if="keywords" class="m-search__clear" @click="clearSearch" :aria-label="$t('mobile.clear')">
        <i class="material-icons">close</i>
      </button>
    </div>

    <div class="m-chips">
      <button class="m-chip" :class="{ 'm-chip--on': !status && completed === null }" @click="selectStatus(null)">{{ $t("mobile.all") }}</button>
      <button v-for="s in statuses" :key="s" class="m-chip" :class="{ 'm-chip--on': status === s }" @click="selectStatus(s)">
        {{ $t("returns.statuses." + s) }}
      </button>
      <!-- "erledigt" ist seit 18.08. ein eigener, allgemeiner Status (nicht Teil des Lagerstatus) -->
      <button class="m-chip" :class="{ 'm-chip--on': completed === true }" @click="selectCompleted">
        {{ $t("returns.generalStatus.completed") }}
      </button>
    </div>

    <div v-if="loading && !items.length" class="m-hint">{{ $t("mobile.loading") }}</div>
    <div v-else-if="!items.length" class="m-hint">{{ $t("mobile.noResults") }}</div>

    <ul class="m-cards">
      <li v-for="item in items" :key="item.id">
        <router-link :to="`/m/retoure/${item.id}`" class="m-card" :class="[`m-ret--${item.status}`, { 'm-ret--completed': item.completedOn }]">
          <div class="m-card__head">
            <!-- Rechnungsnummer prominent (Kundenwunsch 04.08.) - Link zu Afterbuy nur am Desktop -->
            <span class="m-card__title">#{{ item.id }} · {{ item.invoiceNumber || item.senderName || item.referenceNumber || "—" }}</span>
            <span class="m-card__status">
              <span v-if="mp(item)" class="m-mp" :style="{ background: mp(item).color }">{{ mp(item).short }}</span>
              {{ $t("returns.statuses." + item.status) }}<template v-if="item.completedOn"> · {{ $t("returns.generalStatus.completed") }}</template>
            </span>
          </div>
          <p class="m-card__sub">
            {{ $t("returns.types." + item.returnType) }}
            <template v-if="item.customerOrderId"> · {{ item.customerOrderId }}</template>
            <template v-if="item.senderTown"> · {{ item.senderTown }}</template>
            <!-- Ersteller sichtbar machen: im Parallelbetrieb sonst leicht der falsche Entwurf (19.08.) -->
            <template v-if="item.createdBy"> · {{ $t("returns.list.by", { name: item.createdBy }) }}</template>
          </p>
          <p v-if="item.notes" class="m-card__sub m-ret__notes">
            <i class="material-icons">edit_note</i>
            {{ item.notes }}
          </p>
          <div class="m-card__row">
            <i class="material-icons">photo_library</i>
            <span>{{ item.photoCount }}</span>
            <span v-if="item.missingRequiredPhotos" class="m-ret__missing">{{ $t("returns.summary.photosMissing") }}</span>
            <span class="m-ret__date">{{ item.createdOn | formatDateTimeSeconds }}</span>
          </div>
        </router-link>
      </li>
    </ul>

    <button v-if="items.length < total" class="m-more" :disabled="loading" @click="loadMore">
      {{ loading ? $t("mobile.loading") : $t("mobile.loadMore", { count: total - items.length }) }}
    </button>
    <p v-else-if="items.length" class="m-hint m-hint--small">{{ $t("mobile.countAll", { count: total }) }}</p>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import { marketplaceInfo } from "@/helpers/marketplaces";
import ReturnsService from "@/services/returns.service";

const PAGE_SIZE = 20;

export default {
  name: "MobileReturnList",
  data() {
    return {
      items: [],
      statuses: ["captured", "assigned", "checked", "booked"],
      completed: null,
      total: 0,
      pageIndex: 0,
      keywords: "",
      status: null,
      loading: false,
    };
  },
  created() {
    this.onSearchInput = debounce(this.reload, 350);
    this.reload();
  },
  methods: {
    mp(item) {
      return marketplaceInfo(item && item.marketplace);
    },
    selectStatus(s) {
      this.status = s;
      this.completed = null;
      this.reload();
    },
    selectCompleted() {
      this.status = null;
      this.completed = this.completed === true ? null : true;
      this.reload();
    },
    clearSearch() {
      this.keywords = "";
      this.reload();
    },
    async reload() {
      this.pageIndex = 0;
      this.items = [];
      await this.fetch();
    },
    async loadMore() {
      this.pageIndex += 1;
      await this.fetch();
    },
    async fetch() {
      this.loading = true;
      try {
        const payload = await ReturnsService.list({
          pageSize: PAGE_SIZE,
          pageIndex: this.pageIndex,
          keywords: this.keywords || undefined,
          status: this.status || undefined,
          completed: this.completed === null ? undefined : this.completed,
        });
        this.items = this.items.concat(payload.data || []);
        this.total = payload.total || 0;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.m-mp {
  display: inline-block;
  min-width: 28px;
  padding: 1px 4px;
  border-radius: 4px;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  text-align: center;
  margin-right: 4px;
}
.m-list {
  padding: 12px 12px 24px;
}

.m-ret__new {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #7f915a;
  color: #fff !important;
  border-radius: 10px;
  padding: 13px;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 12px;
}

.m-ret__missing {
  font-size: 11px;
  background: #ffebee;
  color: #c62828;
  border-radius: 8px;
  padding: 1px 7px;
}

.m-ret__date {
  margin-left: auto;
  color: #9e9e9e;
  font-size: 12px;
}

.m-ret--booked {
  border-left: 4px solid #7f915a;
}
.m-ret--captured {
  border-left: 4px solid #ff9800;
}
.m-ret--assigned,
.m-ret--checked {
  border-left: 4px solid #2196f3;
}
/* erledigt = allgemeiner Status; gruener Hintergrund (Kundenwunsch 18.08., P14.2) */
.m-ret--completed {
  background: #eff4e6;
}

.m-ret__notes {
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  i {
    font-size: 16px;
    color: #9e9e9e;
    flex: none;
  }
}

// Basis-Stile wie in ShippingPlanList
.m-search {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border-radius: 10px;
  padding: 0 10px;
  height: 44px;
  margin-bottom: 10px;

  i {
    color: #9e9e9e;
    font-size: 20px;
  }
  input {
    flex: 1;
    border: 0;
    outline: none;
    font-size: 15px;
    height: 100%;
    background: none;
  }
  &__clear {
    border: 0;
    background: none;
    color: #9e9e9e;
    cursor: pointer;
  }
}

.m-chips {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 8px;
  margin-bottom: 6px;
}

.m-chip {
  flex: none;
  border: 1px solid #bdbdbd;
  background: #fff;
  color: #616161;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;

  &--on {
    background: #7f915a;
    border-color: #7f915a;
    color: #fff;
  }
}

.m-cards {
  list-style: none;
  margin: 0;
  padding: 0;
}

.m-card {
  display: block;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
  padding: 12px;
  margin-bottom: 10px;
  color: #212121; // Theme-Linkfarbe (lila) nicht erben

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  &__title {
    font-size: 15px;
    font-weight: 500;
  }
  &__status {
    font-size: 12px;
    color: #616161;
    background: #eeeeee;
    border-radius: 8px;
    padding: 2px 8px;
    flex: none;
  }
  &__sub {
    color: #757575;
    font-size: 13px;
    margin: 4px 0;
  }
  &__row {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #616161;

    i {
      font-size: 17px;
      color: #9e9e9e;
    }
  }
}

.m-more {
  display: block;
  width: 100%;
  border: 1px solid #bdbdbd;
  background: #fff;
  border-radius: 10px;
  padding: 12px;
  font-size: 14px;
  color: #424242;
  cursor: pointer;
}

.m-hint {
  text-align: center;
  color: #9e9e9e;
  padding: 18px 0;

  &--small {
    font-size: 12px;
    padding: 10px 0;
  }
}
</style>
